import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { DiceTest } from './DiceTest';
import { AuthGuard } from './components/auth/AuthGuard';
import { PublicWorldsPage } from './components/worlds/PublicWorldsPage';
import { useAuthStore } from './stores/authStore';
import { ThemeProvider } from './themes/ThemeProvider';
import { initConsoleCapture } from './lib/consoleCapture';

// Capture console errors/warnings for the in-app bug reporter.
initConsoleCapture();

// Use ?dice-test in URL to show standalone dice tester
const showDiceTest = window.location.search.includes('dice-test');

/**
 * Public, pre-login front door.
 *
 * `#worlds` renders the Worlds gallery OUTSIDE the AuthGuard so it is reachable
 * without a session (SEO / GEO front door) — but ONLY for logged-OUT visitors.
 * A LOGGED-IN visitor at `#worlds` falls through to `<AuthGuard><App/></AuthGuard>`
 * so that App's own `#worlds` branch handles it: App forwards `autoOpenIntent`
 * to WorldsPage so a post-signup handoff reopens CampaignWizard prefilled.
 *
 * Why gate on auth: the post-signup handoff has App set `window.location.hash =
 * '#worlds'` after consuming the stashed world intent. If this Root rendered
 * PublicWorldsPage unconditionally on that hashchange it would tear down
 * `<AuthGuard><App/></AuthGuard>`, lose the in-memory `worldIntent`, and never
 * reopen the wizard (the sessionStorage key is already consumed). Routing
 * logged-in users back into the app shell keeps the intent alive and the
 * prefilled wizard opens.
 *
 * We still run `authStore.initialize()` so a logged-in visitor keeps their
 * session. While auth is still loading we render the in-app shell (AuthGuard
 * shows its own loading/login UI) rather than flashing the public gallery; once
 * resolved, only a confirmed-logged-out visitor sees PublicWorldsPage.
 *
 * Mirrors the existing `showDiceTest` pre-auth branch.
 */
function Root() {
  const [hash, setHash] = useState(window.location.hash);
  const initialize = useAuthStore((s) => s.initialize);
  const user = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);

  // Keep the session alive even on the public page so logged-in visitors don't
  // get bounced to signup. AuthGuard also calls initialize(); authStore guards
  // against double-init, so calling it from both places is safe.
  useEffect(() => {
    initialize();
  }, [initialize]);

  // SPA nav into/out of `#worlds` without a reload.
  useEffect(() => {
    const onHashChange = () => setHash(window.location.hash);
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  if (showDiceTest) {
    return <DiceTest />;
  }

  // Public Worlds front door — only for confirmed logged-OUT visitors. Logged-in
  // visitors (and any state while auth is still resolving) fall through to the
  // app shell so App's `#worlds` branch can forward the post-signup intent and
  // reopen the wizard prefilled. See the block comment above.
  if (hash === '#worlds' && !isLoading && !user) {
    return <PublicWorldsPage />;
  }

  return (
    <AuthGuard>
      <App />
    </AuthGuard>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider>
      <Root />
    </ThemeProvider>
  </React.StrictMode>
);
